import React from 'react';
import styled from 'styled-components';
import { useZNSName } from '../../hooks/useZNSName';

interface ZNSAddressDisplayProps {
  address: string;
  style?: React.CSSProperties;
  className?: string;
  href?: string;
  target?: string;
  rel?: string;
  onMouseEnter?: (e: React.MouseEvent) => void;
  onMouseLeave?: (e: React.MouseEvent) => void;
  truncateLength?: number; // How many chars to show on each side (default: 6 and 4)
  showIcon?: boolean; // Whether to show ZNS icon next to domain names
  iconSize?: number; // Size of ZNS icon in pixels (default: 14)
  enabled?: boolean; // Whether to enable ZNS lookup (default: true). Set to false for non-Ink chains
}

const ZNSContainer = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const ZNSIcon = styled.img<{ $size?: number }>`
  width: ${props => props.$size || 14}px;
  height: ${props => props.$size || 14}px;
  display: inline-block;
  vertical-align: middle;
  flex-shrink: 0;
`;

/**
 * Component that displays a ZNS domain name if available, otherwise shows truncated address
 * Works for both plain text and links
 * Optionally shows ZNS icon next to domain names
 */
export const ZNSAddressDisplay: React.FC<ZNSAddressDisplayProps> = ({
  address,
  style,
  className,
  href,
  target,
  rel,
  onMouseEnter,
  onMouseLeave,
  truncateLength = 6,
  showIcon = true,
  iconSize = 14,
  enabled = true
}) => {
  const { znsName, isLoading } = useZNSName(address, enabled);
  
  const displayText = znsName || `${address.slice(0, truncateLength)}...${address.slice(-4)}`;
  
  // If loading, show truncated address temporarily
  const content = isLoading ? `${address.slice(0, truncateLength)}...${address.slice(-4)}` : displayText;
  
  // Show icon only if ZNS name exists and showIcon is true
  const shouldShowIcon = showIcon && znsName && !isLoading;
  
  const textContent = (
    <ZNSContainer>
      {shouldShowIcon && <ZNSIcon src="/zns_icon_purple.png" alt="ZNS" $size={iconSize} />}
      {content}
    </ZNSContainer>
  );
  
  if (href) {
    return (
      <a
        href={href}
        target={target}
        rel={rel}
        style={style}
        className={className}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {textContent}
      </a>
    );
  }
  
  return (
    <span style={style} className={className}>
      {textContent}
    </span>
  );
};

